import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SegmentedControl } from ".";
import { CaptionText } from "../../Common/_components/CaptionText";
import { TimeHelper } from "../_helpers";
import { LocalizedDatePicker } from "./LocalizedDatePicker";
import { useTranslation } from "react-i18next";

type IsoRangeDatePickerProps = {
    onChangeStart?: (date: Date | null) => void;
    onChangeEnd?: (date: Date | null) => void;
    begin?: Date | string | null;
    end?: Date | string | null;
    beginPlaceHolder?: string;
    endPlaceHolder?: string;
    startReadOnly?: boolean;
    endReadOnly?: boolean;
    className?: string;
    timezone?: string;
    readOnly?: boolean;
    minDate?: Date;
    maxDate?: Date;
    placeHolder?: string;
    timeInterval?: number;
    position?: "top" | "bottom";
    showTimeSelect?:boolean;
    compact?: boolean;
    usePortal?: boolean;
};

export function IsoRangeDatePicker({
    onChangeStart,
    onChangeEnd,
    beginPlaceHolder,
    endPlaceHolder,
    begin,
    end,
    startReadOnly,
    endReadOnly,
    className,
    timezone,
    readOnly,
    minDate,
    maxDate,
    placeHolder,
    timeInterval = 15,
    position = 'bottom',
    showTimeSelect = true,
    compact = false,
    usePortal = false,
}: IsoRangeDatePickerProps) {

    const componentRef = useRef<any>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    const [startDatePickerOpened, setStartDatePickerOpened] = useState(false);
    const [endDatePickerOpened, setEndDatePickerOpened] = useState(false);
    const [portalPos, setPortalPos] = useState<{
        top: number;
        left?: number;
        right?: number;
    } | null>(null);

    const isOpen = startDatePickerOpened || endDatePickerOpened;

    const startDate = useMemo(() => begin ? new Date(begin) : null, [begin]);
    const endDate = useMemo(() => end ? new Date(end) : null, [end]);

    useEffect(() => {

        function handleClickOutside(event) {
            const insideTrigger =
                componentRef.current &&
                componentRef.current.contains(event.target);
            const insidePopup =
                popupRef.current && popupRef.current.contains(event.target);
            if (!insideTrigger && !insidePopup) {
                setStartDatePickerOpened(false);
                setEndDatePickerOpened(false);
            }
        }

        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [componentRef]);

    // When portaling, anchor the popup with position:fixed to the trigger so it
    // escapes any ancestor with overflow hidden/auto (e.g. a scrollable list).
    useLayoutEffect(() => {
        if (!usePortal || !isOpen) {
            setPortalPos(null);
            return;
        }

        function reposition() {
            const el = componentRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            let top = position === "top" ? rect.top - 6 : rect.bottom + 6;
            // The popup's height is only measurable once it has rendered (the
            // first pass anchors it unclamped, this effect re-runs on the
            // resulting portalPos change). With it known, keep the popup
            // inside the viewport — a trigger near the bottom would otherwise
            // push the calendar off-screen.
            const popupHeight = popupRef.current?.offsetHeight ?? 0;
            if (position !== "top" && popupHeight) {
                top = Math.min(top, window.innerHeight - popupHeight - 8);
                top = Math.max(top, 8);
            }
            // Mirror the inline behaviour: start aligns left, end aligns right.
            const next = startDatePickerOpened
                ? { top, left: rect.left }
                : { top, right: window.innerWidth - rect.right };
            // Bail out on equal positions so the portalPos dependency below
            // can't re-trigger this effect forever.
            setPortalPos(prev =>
                prev &&
                prev.top === next.top &&
                prev.left === next.left &&
                prev.right === next.right
                    ? prev
                    : next,
            );
        }

        reposition();
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [usePortal, isOpen, startDatePickerOpened, position, portalPos]);


    const startDatePicker = () => {
        const onChangeStartDate = (date: Date) => {
            if (readOnly || startReadOnly) return;
            onChangeStart && onChangeStart(date);
            if(endDate && date.getTime() >= endDate.getTime()) {
                onChangeEnd && onChangeEnd(null);
            }
        };

        return (
            <LocalizedDatePicker
                onChange={onChangeStartDate}
                readOnly={readOnly}
                minDate={
                    minDate &&
                    TimeHelper.isoDateToLocalJsDate(minDate, timezone)
                }
                maxDate={
                    maxDate &&
                    TimeHelper.isoDateToLocalJsDate(maxDate, timezone)
                }
                className={`${className ?? ""} rounded p-2`}
                selected={startDate}
                showPopperArrow={false}
                calendarStartDay={1}
                showMonthDropdown
                showYearDropdown
                showTimeSelect={showTimeSelect}
                dropdownMode="select"
                timeIntervals={timeInterval}
                inline
            />
        );
   
    };

    const endDatePicker = () => {


        const onChangeEndDate = (date) => {
            if (readOnly || endReadOnly) return;
            onChangeEnd && onChangeEnd(date);
        };

        const calculatedMinDate = (startDate &&
                        TimeHelper.isoDateToLocalJsDate(startDate, timezone)) ??
                    (minDate &&
                        TimeHelper.isoDateToLocalJsDate(minDate, timezone))

        const calculatedMaxDate = maxDate &&
                    TimeHelper.isoDateToLocalJsDate(maxDate, timezone)
    

        return (
            <LocalizedDatePicker
                onChange={onChangeEndDate}
                readOnly={readOnly}
                minDate={calculatedMinDate}
                maxDate={calculatedMaxDate}
                className={`${className ?? ""} rounded p-2`}
                selected={endDate}
                showPopperArrow={false}
                calendarStartDay={1}
                showMonthDropdown
                showYearDropdown
                showTimeSelect={showTimeSelect}
                dropdownMode="select"
                timeIntervals={timeInterval}
                inline
            />
        );
    };

    const getSegments = () => {

        const DateUILabel = (date, mode: "begin" | "end") => {

            if (!date) {
                return (
                    <div
                        className="c-text-capitalize-first h-100 w-100 px-3 d-flex align-items-center justify-content-center"
                    >
                        {t(mode === 'begin' ? (beginPlaceHolder ?? 'common.from') : (endPlaceHolder ?? 'common.to'))}
                    </div>
                );
            }

            if (compact) {
                return (
                    <div className="px-3 py-1 w-100">
                        <div className="d-flex justify-content-between align-items-center w-100 text-left">
                            <CaptionText
                                color="inherit"
                                className="text-capitalize c-line-height-1 mr-2"
                            >
                                {t(
                                    mode === "begin"
                                        ? "common.from"
                                        : "common.to",
                                )}
                            </CaptionText>
                            <div className="text-right" style={{ whiteSpace: "nowrap" }}>
                                {TimeHelper.localizeIsoStringToFormat(
                                    date,
                                    timezone,
                                    "DD MMM YYYY",
                                )}
                                {showTimeSelect &&
                                    " · " +
                                        TimeHelper.localizeIsoStringToFormat(
                                            date,
                                            timezone,
                                            "HH:mm",
                                        ) +
                                        "h"}
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <div className="px-3 pt-2 pb-1 w-100">
                    <div className="w-100 text-left">
                        <div className="d-flex justify-content-between">

                            <CaptionText
                                color="inherit"
                                className="text-capitalize c-line-height-1"
                            >
                                {t(
                                    mode === "begin"
                                        ? "common.from"
                                        : "common.to",
                                )}
                            </CaptionText>
                            {showTimeSelect && (<CaptionText
                                color="inherit"
                                className="text-capitalize text-right c-line-height-1"
                            >
                                {TimeHelper.localizeIsoStringToFormat(
                                    date,
                                    timezone,
                                    "HH:mm",
                                )}
                                h
                            </CaptionText>
                            )}
                        </div>
                        <div style={{ whiteSpace: "normal" }}>
                            {TimeHelper.localizeIsoStringToFormat(
                                date,
                                timezone,
                                "ddd DD MMM YYYY",
                            )}
                        </div>
                    </div>
                </div>
            );
        };

        if (!(startDate || endDate)) {
            return [
                {
                    label: placeHolder,
                    onClick: () => {
                        setEndDatePickerOpened(false);
                        setStartDatePickerOpened(true);
                    },
                },
            ];
        } else {
            return [
                {
                    label: DateUILabel(startDate, "begin"),
                    onClick: () => {
                        setEndDatePickerOpened(false);
                        setStartDatePickerOpened(true);
                    },
                },
                {
                    label: DateUILabel(endDate, "end"),
                    onClick: () => {
                        setStartDatePickerOpened(false);
                        setEndDatePickerOpened(true);
                    },
                },
            ];
        }
    };

    return (
        <div ref={componentRef}>
            <SegmentedControl
                preventDefaultSelect={true}
                // The control starts with the first segment highlighted but no
                // calendar open; without allowSingle a click on the active
                // segment is swallowed, so the start date could only be opened
                // after visiting the end date first. It also keeps the
                // single-segment placeholder state clickable.
                allowSingle
                readOnly={readOnly}
                segments={getSegments()}
                equalSegments={!compact}
                fullWidth={!compact}
            >
                {!(startDate || startDate) && (
                    <CaptionText>{placeHolder ?? ""}</CaptionText>
                )}
                {isOpen && !usePortal && (
                    <div
                        style={{
                            width: "max-content",
                            bottom:
                                position === "top" ? "calc(100% + 6px)" : "",
                        }}
                        className={`${
                            startDatePickerOpened ? "left-0" : "right-0"
                        } position-absolute mt-2 c-z-index-full`}
                    >
                        {startDatePickerOpened && startDatePicker()}
                        {endDatePickerOpened && endDatePicker()}
                    </div>
                )}
                {isOpen &&
                    usePortal &&
                    portalPos &&
                    createPortal(
                        <div
                            ref={popupRef}
                            style={{
                                position: "fixed",
                                top: portalPos.top,
                                left: portalPos.left,
                                right: portalPos.right,
                                width: "max-content",
                                transform:
                                    position === "top"
                                        ? "translateY(-100%)"
                                        : undefined,
                            }}
                            className="c-z-index-full"
                        >
                            {startDatePickerOpened && startDatePicker()}
                            {endDatePickerOpened && endDatePicker()}
                        </div>,
                        document.body,
                    )}
            </SegmentedControl>
        </div>
    );
}
